import { useEffect, useMemo, useState } from "react";
import { useIntl, FormattedMessage as FM } from "react-intl";
import { signal } from "@preact/signals-react";

import proxy from "api/proxy";
import { getStorage } from "store/local";
import ImageQr from "components/Tools/ImageQr";
import StdButton from "components/Buttons/StdButton";
import CharField from "components/MeForm/CharField";
import CheckboxField from "components/MeForm/CheckboxField";
import { Alert } from "componentsv2/widgets/Alert";
import ColorPickerField from "../Components/ColorPickerField";
import { getContrastText } from "../helpers/colors";
import {
  MELHOUS_MODEL,
  QR_LINKS,
  COLOR_FIELDS,
  SECTION_FIELDS,
  APPEARANCE_FIELDS,
} from "../constants";

const attrs = { level: "wizard" };

function createSignals() {
  return Object.fromEntries(
    APPEARANCE_FIELDS.map((name) => [name, signal(null)]),
  );
}

function getQrBase(linkMelhous) {
  if (linkMelhous) return linkMelhous.replace(/\/+$/, "");
  const { db } = getStorage("ctxSession") || {};
  return `${window.location.protocol}//${window.location.host}/app/${db}/web_melhous`;
}

function getCreatedId(data) {
  const created = Array.isArray(data) ? data[0] : data;
  return created?.id ?? created ?? null;
}

function SectionTitle({ id }) {
  return (
    <h3 className="col-span-2 text-lg font-bold uppercase text-neutral-700">
      <FM id={id} />
    </h3>
  );
}

function ThemePreview({ values }) {
  const primary = values.primary_color.value || "#1f3a5f";
  const secondary = values.secondary_color.value || "#f2a541";
  const accent = values.accent_color.value || "#ff5733";
  const text = values.text_color.value || "#1a1a1a";
  const surface = values.surface_color.value || "#ffffff";
  const border = values.border_color.value || "#e5e7eb";

  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: border, backgroundColor: surface, color: text }}
    >
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ backgroundColor: primary, color: getContrastText(primary) }}
      >
        <span className="text-sm font-bold uppercase tracking-wide">
          Melhous
        </span>
        <span
          className="size-3 rounded-full"
          style={{ backgroundColor: secondary }}
        />
      </div>
      <div className="space-y-3 p-4">
        <p className="text-base font-semibold">
          <FM id="melhous.dash.preview.title" />
        </p>
        <div
          className="rounded-lg border p-3 text-sm"
          style={{ borderColor: border }}
        >
          <FM id="melhous.dash.preview.text" />
        </div>
        <button
          type="button"
          className="w-full rounded-full px-4 py-2 text-sm font-medium uppercase"
          style={{ backgroundColor: accent, color: getContrastText(accent) }}
        >
          <FM id="melhous.dash.preview.button" />
        </button>
      </div>
    </div>
  );
}

function ConfigColor() {
  const intl = useIntl();
  const values = useMemo(createSignals, []);
  const [recordId, setRecordId] = useState(null);
  const [linkMelhous, setLinkMelhous] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const qrBase = getQrBase(linkMelhous);

  async function getConfig() {
    const { data } = await proxy.search(
      MELHOUS_MODEL,
      [],
      ["id", "link_melhous", ...APPEARANCE_FIELDS],
      1,
    );
    const current = data?.[0];
    if (!current) return;
    setRecordId(current.id);
    setLinkMelhous(current.link_melhous || null);
    APPEARANCE_FIELDS.forEach((name) => {
      values[name].value = current[name] ?? null;
    });
  }

  async function saveConfig() {
    const storeRec = Object.fromEntries(
      APPEARANCE_FIELDS.map((name) => [name, values[name].value || null]),
    );
    SECTION_FIELDS.forEach((name) => {
      storeRec[name] = Boolean(values[name].value);
    });
    if (recordId) storeRec.id = recordId;
    const res = await proxy.saveQuery({
      model: MELHOUS_MODEL,
      storeRec,
      fieldsNames: ["id"],
    });
    if (!res || res.error) return;
    if (!recordId) setRecordId(getCreatedId(res.data));
    setShowAlert(true);
  }

  function closeAlert() {
    setShowAlert(false);
  }

  useEffect(() => {
    getConfig();
  }, []);

  return (
    <div className="w-full style-scroll">
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
        <div className="md:col-span-2 space-y-4">
          <ThemePreview values={values} />
          <div className="bg-gray-100 rounded-md p-4 space-y-3 divide-y divide-gray-300">
            {QR_LINKS.map((qr) => (
              <ImageQr
                key={qr.path}
                value={`${qrBase}${qr.path}`}
                name={qr.name}
                title={intl.formatMessage({ id: qr.label })}
              />
            ))}
          </div>
        </div>
        <div className="md:col-span-4 py-4 space-y-6">
          <div className="grid grid-cols-2 gap-x-4">
            <SectionTitle id="melhous.dash.colors" />
            {COLOR_FIELDS.map((name) => (
              <ColorPickerField
                key={name}
                field={{ name, label: `melhous.${name}`, type: "char" }}
                data={values[name]}
                attrs={attrs}
              />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-x-4">
            <SectionTitle id="melhous.dash.sections" />
            {SECTION_FIELDS.map((name) => (
              <CheckboxField
                key={name}
                field={{ name, label: `melhous.${name}`, type: "boolean" }}
                data={values[name]}
                attrs={attrs}
              />
            ))}
            <CharField
              field={{
                name: "logo_link",
                label: "melhous.logo_link",
                placeholder: "https://",
              }}
              data={values.logo_link}
              attrs={attrs}
            />
          </div>
          <Alert
            text="melhous.dash.saved"
            show={showAlert}
            onClose={closeAlert}
            color="bg-green-200 text-green-800"
            icon="check"
            autoRemove={true}
          />
          <div className="pb-3 w-full">
            <StdButton
              onClick={saveConfig}
              color="black"
              size="w-full"
              style="mx-auto uppercase"
              content={recordId ? "melhous.dash.save" : "melhous.dash.create"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConfigColor;
